import React, { useState, useEffect } from 'react';
import './Reportsanlysics.css';
import { 
  FaUsers, FaUserPlus, FaUserCheck, FaChartLine, FaCalendarAlt, 
  FaDownload, FaFilter, FaEye, FaMousePointer, FaClock, 
  FaArrowUp, FaArrowDown, FaRegClock, FaShoppingCart, 
  FaMoneyBillWave, FaStar, FaRegChartBar, FaSyncAlt,
  FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaGlobe
} from 'react-icons/fa';

const UserAnalytics = () => {
  // State for date range
  const [dateRange, setDateRange] = useState('week');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState('all');
  
  // Demo Analytics Data
  const [analyticsData, setAnalyticsData] = useState({
    // Key Metrics
    metrics: {
      totalUsers: 15420,
      newUsers: 1245,
      activeUsers: 8920,
      returningUsers: 6780,
      avgSessionTime: "4m 32s",
      bounceRate: "34.2%",
      conversionRate: "12.8%",
      totalRevenue: 2845000,
      avgOrderValue: 1845,
      totalOrders: 15420
    },
    
    // Weekly/Monthly Trends
    trends: {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      users: [2100, 2450, 2680, 2920, 3450, 3980, 3670],
      revenue: [285000, 312000, 348000, 395000, 468000, 542000, 495000],
      orders: [1850, 2120, 2340, 2680, 3120, 3680, 3450]
    },
    
    // User Demographics
    demographics: {
      ageGroups: [
        { group: '18-24', percentage: 18, users: 2776 },
        { group: '25-34', percentage: 42, users: 6476 },
        { group: '35-44', percentage: 24, users: 3701 },
        { group: '45-54', percentage: 10, users: 1542 },
        { group: '55+', percentage: 6, users: 925 }
      ],
      locations: [
        { city: 'Mumbai', users: 3240, percentage: 21 },
        { city: 'Delhi', users: 2890, percentage: 18.7 },
        { city: 'Bangalore', users: 2650, percentage: 17.2 },
        { city: 'Hyderabad', users: 1890, percentage: 12.3 },
        { city: 'Chennai', users: 1670, percentage: 10.8 },
        { city: 'Kolkata', users: 1430, percentage: 9.3 },
        { city: 'Others', users: 1650, percentage: 10.7 }
      ],
      devices: [
        { type: 'Mobile', percentage: 68, users: 10486 },
        { type: 'Desktop', percentage: 22, users: 3392 },
        { type: 'Tablet', percentage: 10, users: 1542 }
      ]
    },
    
    // User Engagement
    engagement: {
      avgTimeOnSite: "4m 32s",
      pagesPerSession: 3.8,
      sessionPerUser: 2.4,
      returningRate: "43.8%"
    },
    
    // Recent User Activities
    recentActivities: [
      { id: 1, user: "Rahul Sharma", action: "Completed Order", amount: "₹2,450", time: "2 min ago", status: "success" },
      { id: 2, user: "Priya Patel", action: "Signed Up", time: "15 min ago", status: "success" },
      { id: 3, user: "Amit Kumar", action: "Added to Cart", amount: "₹890", time: "32 min ago", status: "pending" },
      { id: 4, user: "Neha Singh", action: "Left Review", rating: 5, time: "1 hour ago", status: "success" },
      { id: 5, user: "Vikram Mehta", action: "Abandoned Cart", amount: "₹3,200", time: "2 hours ago", status: "warning" },
      { id: 6, user: "Sneha Reddy", action: "Completed Order", amount: "₹5,670", time: "3 hours ago", status: "success" },
      { id: 7, user: "Arjun Nair", action: "Viewed Product", product: "Premium Plan", time: "4 hours ago", status: "info" }
    ],
    
    // Growth Stats
    growthStats: {
      weekOverWeek: { users: "+12.5%", revenue: "+15.3%", orders: "+11.8%" },
      monthOverMonth: { users: "+34.2%", revenue: "+42.1%", orders: "+38.5%" }
    }
  });
  
  // Top Users by engagement
  const [topUsers, setTopUsers] = useState([
    { rank: 1, name: "Rahul Sharma", email: "rahul@example.com", orders: 24, spent: "₹58,400", lastActive: "2 min ago" },
    { rank: 2, name: "Priya Patel", email: "priya@example.com", orders: 19, spent: "₹46,200", lastActive: "15 min ago" },
    { rank: 3, name: "Amit Kumar", email: "amit@example.com", orders: 17, spent: "₹41,800", lastActive: "32 min ago" },
    { rank: 4, name: "Neha Singh", email: "neha@example.com", orders: 15, spent: "₹37,500", lastActive: "1 hour ago" },
    { rank: 5, name: "Vikram Mehta", email: "vikram@example.com", orders: 13, spent: "₹32,900", lastActive: "2 hours ago" }
  ]);
  
  // Chart refs and state for responsive charts
  const [chartWidth, setChartWidth] = useState(0);
  const chartContainerRef = React.useRef(null);
  
  useEffect(() => {
    const handleResize = () => {
      if (chartContainerRef.current) {
        setChartWidth(chartContainerRef.current.offsetWidth);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Simulate data refresh
  const refreshData = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 800);
  };
  
  // Format number with K/M suffix
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Render bar chart (pure CSS/HTML)
  const renderBarChart = (data, color, maxValue = null) => {
    const values = data;
    const max = maxValue || Math.max(...values);
    
    return (
      <div className="bar-chart">
        {values.map((value, idx) => (
          <div key={idx} className="bar-chart-item">
            <div className="bar-chart-bar-wrapper">
              <div 
                className="bar-chart-bar" 
                style={{ 
                  height: `${(value / max) * 100}%`,
                  backgroundColor: color,
                  width: '100%'
                }}
              ></div>
            </div>
            <div className="bar-chart-label">{analyticsData.trends.labels[idx]}</div>
          </div>
        ))}
      </div>
    );
  };
  
  // Render doughnut/pie chart (CSS based)
  const renderDonutChart = (percentage, color, size = 80) => {
    const circumference = 2 * Math.PI * 35;
    const offset = circumference - (percentage / 100) * circumference;
    
    return (
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="35" fill="none" stroke="#e2e8f0" strokeWidth="10" />
        <circle 
          cx="50" cy="50" r="35" fill="none" 
          stroke={color} 
          strokeWidth="10" 
          strokeDasharray={circumference} 
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 50 50)"
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
        <text x="50" y="55" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#1e293b">
          {percentage}%
        </text>
      </svg>
    );
  };
  
  return (
    <div className="analytics-container">
      {/* Header Section */}
      <div className="analytics-header">
        <div className="header-left">
          <h1 className="analytics-title">
            <FaRegChartBar className="title-icon" />
            User Reports & Analytics
          </h1>
          <p className="analytics-subtitle">Track user behavior, engagement metrics, and growth trends</p>
        </div>
        <div className="header-right">
          <div className="date-filter">
            <FaCalendarAlt className="filter-icon" />
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
          </div>
          <button className="refresh-btn" onClick={refreshData} disabled={isLoading}>
            <FaSyncAlt className={isLoading ? 'spinning' : ''} />
          </button>
          <button className="export-btn">
            <FaDownload /> Export Report
          </button>
        </div>
      </div>
      
      {/* Key Metrics Grid */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon total-users">
            <FaUsers />
          </div>
          <div className="metric-info">
            <span className="metric-label">Total Users</span>
            <span className="metric-value">{formatNumber(analyticsData.metrics.totalUsers)}</span>
            <span className="metric-trend positive">
              <FaArrowUp /> +12.5%
            </span>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon new-users">
            <FaUserPlus />
          </div>
          <div className="metric-info">
            <span className="metric-label">New Users (30d)</span>
            <span className="metric-value">{formatNumber(analyticsData.metrics.newUsers)}</span>
            <span className="metric-trend positive">
              <FaArrowUp /> +8.3%
            </span>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon active-users">
            <FaUserCheck />
          </div>
          <div className="metric-info">
            <span className="metric-label">Active Users</span>
            <span className="metric-value">{formatNumber(analyticsData.metrics.activeUsers)}</span>
            <span className="metric-trend positive">
              <FaArrowUp /> +5.2%
            </span>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon conversion">
            <FaChartLine />
          </div>
          <div className="metric-info">
            <span className="metric-label">Conversion Rate</span>
            <span className="metric-value">{analyticsData.metrics.conversionRate}</span>
            <span className="metric-trend positive">
              <FaArrowUp /> +2.1%
            </span>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon revenue">
            <FaMoneyBillWave />
          </div>
          <div className="metric-info">
            <span className="metric-label">Total Revenue</span>
            <span className="metric-value">{formatCurrency(analyticsData.metrics.totalRevenue)}</span>
            <span className="metric-trend positive">
              <FaArrowUp /> +15.3%
            </span>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon orders">
            <FaShoppingCart />
          </div>
          <div className="metric-info">
            <span className="metric-label">Total Orders</span>
            <span className="metric-value">{formatNumber(analyticsData.metrics.totalOrders)}</span>
            <span className="metric-trend positive">
              <FaArrowUp /> +11.8%
            </span>
          </div>
        </div>
      </div>
      
      {/* Charts Section */}
      <div className="charts-row">
        {/* User Trends Chart */}
        <div className="chart-card large">
          <div className="chart-header">
            <h3>User & Revenue Trends</h3>
            <div className="chart-legend">
              <span><span className="legend-dot users"></span> Users</span>
              <span><span className="legend-dot revenue"></span> Revenue</span>
            </div>
          </div>
          <div className="chart-body" ref={chartContainerRef}>
            <div className="dual-chart">
              <div className="chart-area">
                <div className="chart-y-axis">
                  <span>10K</span>
                  <span>7.5K</span>
                  <span>5K</span>
                  <span>2.5K</span>
                  <span>0</span>
                </div>
                <div className="bar-chart-container">
                  {renderBarChart(analyticsData.trends.users, '#8b5cf6', 5000)}
                </div>
              </div>
              <div className="chart-area">
                <div className="chart-y-axis">
                  <span>₹600K</span>
                  <span>₹450K</span>
                  <span>₹300K</span>
                  <span>₹150K</span>
                  <span>₹0</span>
                </div>
                <div className="bar-chart-container">
                  {renderBarChart(analyticsData.trends.revenue, '#10b981', 600000)}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Demographics */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>User Demographics</h3>
            <FaGlobe className="chart-icon" />
          </div>
          <div className="chart-body demographics-body">
            <div className="demographics-section">
              <h4>Age Groups</h4>
              {analyticsData.demographics.ageGroups.map((age, idx) => (
                <div key={idx} className="demographic-bar">
                  <span className="demographic-label">{age.group}</span>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${age.percentage}%`, backgroundColor: '#3b82f6' }}></div>
                  </div>
                  <span className="demographic-percent">{age.percentage}%</span>
                </div>
              ))}
            </div>
            
            <div className="demographics-section">
              <h4>Device Distribution</h4>
              <div className="device-stats">
                {analyticsData.demographics.devices.map((device, idx) => (
                  <div key={idx} className="device-item">
                    <div className="device-info">
                      <span className="device-type">{device.type}</span>
                      <span className="device-percent">{device.percentage}%</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${device.percentage}%`, backgroundColor: '#f59e0b' }}></div>
                    </div>
                    <span className="device-users">{formatNumber(device.users)} users</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Engagement & Location Section */}
      <div className="charts-row">
        {/* Top Locations */}
        <div className="chart-card">
          <div className="chart-header">
            <h3><FaMapMarkerAlt /> Top Locations</h3>
          </div>
          <div className="chart-body locations-body">
            {analyticsData.demographics.locations.slice(0, 5).map((loc, idx) => (
              <div key={idx} className="location-item">
                <div className="location-info">
                  <span className="location-name">{loc.city}</span>
                  <span className="location-users">{formatNumber(loc.users)} users</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${loc.percentage}%`, backgroundColor: '#ec489a' }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Engagement Metrics */}
        <div className="chart-card">
          <div className="chart-header">
            <h3><FaClock /> User Engagement</h3>
          </div>
          <div className="chart-body engagement-body">
            <div className="engagement-grid">
              <div className="engagement-item">
                <span className="engagement-value">{analyticsData.engagement.avgTimeOnSite}</span>
                <span className="engagement-label">Avg. Session Duration</span>
              </div>
              <div className="engagement-item">
                <span className="engagement-value">{analyticsData.engagement.pagesPerSession}</span>
                <span className="engagement-label">Pages / Session</span>
              </div>
              <div className="engagement-item">
                <span className="engagement-value">{analyticsData.engagement.sessionPerUser}</span>
                <span className="engagement-label">Sessions / User</span>
              </div>
              <div className="engagement-item">
                <span className="engagement-value">{analyticsData.engagement.returningRate}</span>
                <span className="engagement-label">Returning Rate</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Growth Stats */}
        <div className="chart-card">
          <div className="chart-header">
            <h3><FaChartLine /> Growth Overview</h3>
          </div>
          <div className="chart-body growth-body">
            <div className="growth-compare">
              <div className="growth-period">
                <h4>Week over Week</h4>
                <div className="growth-stats">
                  <div className="growth-stat">
                    <span className="growth-label">Users</span>
                    <span className="growth-value positive">{analyticsData.growthStats.weekOverWeek.users}</span>
                  </div>
                  <div className="growth-stat">
                    <span className="growth-label">Revenue</span>
                    <span className="growth-value positive">{analyticsData.growthStats.weekOverWeek.revenue}</span>
                  </div>
                  <div className="growth-stat">
                    <span className="growth-label">Orders</span>
                    <span className="growth-value positive">{analyticsData.growthStats.weekOverWeek.orders}</span>
                  </div>
                </div>
              </div>
              <div className="growth-period">
                <h4>Month over Month</h4>
                <div className="growth-stats">
                  <div className="growth-stat">
                    <span className="growth-label">Users</span>
                    <span className="growth-value positive">{analyticsData.growthStats.monthOverMonth.users}</span>
                  </div>
                  <div className="growth-stat">
                    <span className="growth-label">Revenue</span>
                    <span className="growth-value positive">{analyticsData.growthStats.monthOverMonth.revenue}</span>
                  </div>
                  <div className="growth-stat">
                    <span className="growth-label">Orders</span>
                    <span className="growth-value positive">{analyticsData.growthStats.monthOverMonth.orders}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Top Users Table */}
      <div className="data-table-card">
        <div className="table-header">
          <h3>🏆 Top Users by Engagement</h3>
          <button className="view-all-btn">View All →</button>
        </div>
        <div className="table-container">
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>User Name</th>
                <th>Email</th>
                <th>Total Orders</th>
                <th>Total Spent</th>
                <th>Last Active</th>
              </tr>
            </thead>
            <tbody>
              {topUsers.map((user) => (
                <tr key={user.rank}>
                  <td className="rank-cell">#{user.rank}</td>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.orders} orders</td>
                  <td className="spent-cell">{user.spent}</td>
                  <td className="active-cell">{user.lastActive}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Recent Activities */}
      <div className="data-table-card">
        <div className="table-header">
          <h3>🕐 Recent User Activities</h3>
          <FaRegClock className="section-icon" />
        </div>
        <div className="activities-list">
          {analyticsData.recentActivities.map((activity) => (
            <div key={activity.id} className={`activity-item ${activity.status}`}>
              <div className="activity-avatar">
                {activity.user.charAt(0)}
              </div>
              <div className="activity-details">
                <div className="activity-user">{activity.user}</div>
                <div className="activity-action">{activity.action}</div>
                {activity.amount && <div className="activity-amount">{activity.amount}</div>}
                {activity.rating && <div className="activity-rating">{"★".repeat(activity.rating)}</div>}
              </div>
              <div className="activity-time">{activity.time}</div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Summary Footer */}
      <div className="analytics-footer">
        <div className="footer-stats">
          <div className="footer-stat">
            <FaEye />
            <span>Last updated: Today at 10:30 AM</span>
          </div>
          <div className="footer-stat">
            <FaClock />
            <span>Data range: {dateRange === 'week' ? 'Last 7 days' : dateRange === 'month' ? 'Last 30 days' : 'Custom range'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAnalytics;